import React, { useState, useEffect } from 'react';
import { Modal, Select, Input, Button, Typography, Checkbox, Form, DatePicker, Divider, message } from 'antd';
import { CheckCircle, X, Users as UsersIcon, Bell, Clock } from 'lucide-react';
import dayjs from 'dayjs';
import dialerService from '../services/dialerService';
import { getSession } from '../../../getSession';

const { Text } = Typography;
const { TextArea } = Input;

const TaskModalV2 = ({ isOpen, onClose, record, users: propUsers = [] }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [activityTypes, setActivityTypes] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);

  const users = propUsers.length > 0 ? propUsers : internalUsers;

  useEffect(() => {
    if (isOpen && propUsers.length === 0 && internalUsers.length === 0) {
      fetchUsers();
    }
  }, [isOpen, propUsers.length, internalUsers.length]);

  useEffect(() => {
    if (isOpen && record) {
      fetchInitialData();
      handleReset();
    }
  }, [isOpen, record, users]);

  const fetchUsers = async () => {
    if (loadingUsers) return;
    setLoadingUsers(true);
    try {
      const response = await dialerService.getHierarchyUsers();
      if (response && Array.isArray(response)) {
        const mapped = response
          .filter(u => u && (u.Code || u.Value || u.Id || u.UserId || u.UserID))
          .map(u => ({
            label: u.Text || u.Name || u.UserName || u.label || 'Unknown User',
            value: String(u.Code || u.Value || u.Id || u.UserId || u.UserID)
          }));
        setInternalUsers(mapped);
      }
    } catch (e) {
      console.error('Modal fetch users error:', e);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchInitialData = async () => {
    try {
      setActivityTypes([
        { label: 'Facebook Chat', value: '7' },
        { label: 'service', value: '394' },
        { label: 'Critical visit', value: '498' },
        { label: 'KYC Verfication', value: '654' },
        { label: 'OTP', value: '665' },
        { label: 'Printing', value: '720' },
        { label: 'Dispathed', value: '721' },
        { label: 'Purchased order', value: '722' },
        { label: 'Project', value: '728' },
      ]);
      setOutcomes([
        { label: 'Converted', value: '1' },
        { label: 'Wrong Number', value: '2' },
        { label: 'Dead Lead', value: '3' },
        { label: 'Not-Interested', value: '4' },
        { label: 'Call-Back', value: '5' },
      ]);
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
    }
  };

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      const { userId } = getSession();
      const taskData = {
        Id: 0,
        Title: values.title,
        Description: values.description,
        Remarks: values.remarks,
        StartDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD HH:mm:ss') : null,
        EndDate: values.dueDate ? values.dueDate.format('YYYY-MM-DD HH:mm:ss') : null,
        LeadId: record.Id,
        AssignedTo: Number(values.owner),
        CreatedBy: userId,
        IsTask: true,
        IsAppointment: false,
        ActivityType: values.activityType,
        CollaboratorUserIds: values.collaborators ? values.collaborators.join(',') : '',
        Status: 'open'
      };

      const res = await dialerService.insertActivity(taskData);
      
      if (res && res.IsInserted) {
        message.success('Task added successfully!');
        onClose();
      } else {
        message.error(res?.Message || 'Failed to save task');
      }
    } catch (error) {
      console.error('Failed to save task:', error);
      message.error('An error occurred while saving the task.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    if (record) {
      form.setFieldsValue({
        title: `Task for ${record?.PersonName || record?.PersonMobileNo || record?.MobileNo || 'Customer'}`,
        dueDate: dayjs().add(1, 'day').hour(10).minute(0),
        owner: users && users.length > 0 ? users[0].value : undefined,
        activityType: '7'
      });
    }
  };

  if (!record) return null;

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <CheckCircle size={20} className="text-emerald-600" />
          <span className="text-lg font-bold text-slate-800">Add Task</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={650}
      className="premium-modal"
      closeIcon={<X size={20} className="text-slate-400 hover:text-slate-600" />}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        className="py-4 max-h-[75vh] overflow-y-auto px-2 custom-scrollbar"
      >
        <div className="space-y-4">
          <Form.Item
            name="title"
            label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Title</span>}
            rules={[{ required: true, message: 'Please Enter Title' }]}
          >
            <Input placeholder="Please Enter Title" className="h-11 rounded-xl shadow-sm border-slate-200" />
          </Form.Item>

          <Form.Item
            name="description"
            label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Description</span>}
          >
            <TextArea rows={2} placeholder="Start Typing the details about the task." className="rounded-xl shadow-sm border-slate-200" />
          </Form.Item>

          <Form.Item
            name="remarks"
            label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Remarks</span>}
          >
            <TextArea rows={2} placeholder=" about the task..." className="rounded-xl shadow-sm border-slate-200" />
          </Form.Item>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="activityType"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Sales Activity Type</span>}
              rules={[{ required: true, message: 'Select activity type' }]}
            >
              <Select
                placeholder="---Select Sales activity---"
                options={activityTypes}
                className="h-11 premium-select"
                getPopupContainer={trigger => trigger.parentNode}
                virtual={false}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item
              name="dueDate"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Due Date and Time</span>}
              rules={[{ required: true, message: 'Select due date' }]}
            >
              <DatePicker
                showTime
                format="DD-MMM-YYYY HH:mm"
                className="w-full h-11 rounded-xl shadow-sm border-slate-200"
                getPopupContainer={trigger => trigger.parentNode}
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item
              name="owner"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest flex items-center gap-1"><span className="text-red-500">*</span> Owner</span>}
              rules={[{ required: true, message: 'Select owner' }]}
            >
              <Select 
                placeholder="---Select Owner---" 
                options={users} 
                className="h-11 premium-select" 
                getPopupContainer={trigger => trigger.parentNode}
                virtual={false}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>

            <Form.Item
              name="collaborators"
              label={<span className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Collaborators</span>}
            >
              <Select
                mode="multiple"
                placeholder="Select Collaborators"
                options={users}
                className="min-h-11 premium-select"
                getPopupContainer={trigger => trigger.parentNode}
                virtual={false}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-10 border-t border-slate-100 pt-6">
          <Button 
            onClick={handleReset} 
            className="rounded-xl font-black text-[11px] uppercase tracking-widest h-12 px-8 border-slate-200 text-slate-500 hover:text-slate-700 hover:border-slate-300 transition-all shadow-sm"
          >
            Reset
          </Button>
          <Button 
            type="primary" 
            htmlType="submit"
            loading={loading}
            className="rounded-xl font-black text-[11px] uppercase tracking-widest h-12 px-12 bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-200 border-none flex items-center gap-2 transition-all"
          >
            Save
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default TaskModalV2;
